import { Box } from '@mui/material';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroller';
import { useSelector } from 'react-redux';

import {
	getGeneratedArtifacts as getGeneratedArtifactsAction,
	refreshGeneratedPodcastArtifact as refreshGeneratedPodcastArtifactAction,
} from '../../actions/aiAssistant.actions';
import { customSizes, palette, spaces } from '../../assets/styles/themes/tokens';
import MasterLayout from '../../components/AppLayout/MasterLayout';
import EmptySearchStateImage from '../../components/Search/SearchResults/EmptySearchResultsNewUI/EmptySearchStateImage';
import SearchResultsLoader from '../../components/Search/SearchResults/SearchResultsLoader';
import { aiAssistantPageSource } from '../../constants';
import { AiLoadingStatus } from '../../reducers/aiAssistant.reducers';
import {
	getArtifactsLoadingStatus,
	getGeneratedArtifactsData,
	getGeneratedArtifactsHasNext,
} from '../../reducers/aiAssistant.selectors';
import services from '../../services';
import { MasterLayoutContext } from '../../state/MasterLayoutProvider';
import { isGeneratingPodcastArtifact } from '../../utils/artifacts';
import useAction from '../../utils/useAction';
import useScreenSize from '../../utils/useScreenSize';
import { useNewUiSearchPagePadding } from '../courses/CourseSearchPage/CourseSearchPage';
import ArtifactCard from './ArtifactCard/ArtifactCard';
import MyContentsSearchBar from './MyContentsSearchBar/MyContentsSearchBar';
import MyContentsTopBarContent from './MyContentsTopBarContent';

const ARTIFACTS_POLL_INTERVAL_MS = 5000;

const MyContentsEmptyState: React.FC = () => {
	const { t } = useTranslation();
	return (
		<EmptySearchStateImage
			title={t('Still no materials')}
			subtitle={t(
				'All AI-created materials that you create with Muki will automatically appear here.'
			)}
			iconName="emptyState"
		/>
	);
};

const MyContentsEmptySearchState: React.FC<{
	submittedSearchTitle: string;
}> = ({ submittedSearchTitle }) => {
	const { t } = useTranslation();
	return (
		<EmptySearchStateImage
			title={t('No materials found for your search.')}
			subtitle={
				<Trans
					i18nKey='No results for "<0>{{searchTerm}}</0>". Try a different search term.'
					values={{ searchTerm: submittedSearchTitle }}
					components={[
						<Box key="0" component="span" sx={{ fontWeight: '500', color: palette.text.secondary }} />,
					]}
				/>
			}
			iconName="emptyState"
			subtitleSx={{
				maxWidth: customSizes.emptyStateSubtitleMaxWidth,
				textAlign: 'center',
			}}
		/>
	);
};

const MyContentsPage: React.FC = () => {
	const getGeneratedArtifacts = useAction(getGeneratedArtifactsAction);
	const refreshGeneratedPodcastArtifact = useAction(refreshGeneratedPodcastArtifactAction);
	const { isXsScreen, isSmScreen } = useScreenSize();
	const isSmallScreen = isXsScreen || isSmScreen;
	const contentPadding = useNewUiSearchPagePadding();
	const artifacts = useSelector(getGeneratedArtifactsData);
	const loadingStatus = useSelector(getArtifactsLoadingStatus);
	const hasNext = useSelector(getGeneratedArtifactsHasNext);
	const { mainContentContainer } = useContext(MasterLayoutContext);
	const [titleFilter, setTitleFilter] = useState('');
	const [submittedSearchTitle, setSubmittedFilter] = useState<string | undefined>(undefined);
	const [showSearchBar, setShowSearchBar] = useState(false);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		getGeneratedArtifacts();
	}, []);

	useEffect(() => {
		if (artifacts.length > 0) {
			setShowSearchBar(true);
		}
	}, [artifacts]);

	// Poll only the generating podcasts and patch them into the grid in place.
	const generatingPodcastIdsKey = artifacts
		.filter(isGeneratingPodcastArtifact)
		.map((artifact) => artifact.id)
		.join(',');

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (!generatingPodcastIdsKey) return;

		const podcastIds = generatingPodcastIdsKey.split(',');
		const intervalId = setInterval(() => {
			// biome-ignore lint/suspicious/useIterableCallbackReturn: pre-existing callback shape; changing returns needs per-site behavior review
			podcastIds.forEach((podcastId) => refreshGeneratedPodcastArtifact({ podcastId }));
		}, ARTIFACTS_POLL_INTERVAL_MS);

		return () => clearInterval(intervalId);
	}, [generatingPodcastIdsKey]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	const handleSearch = useCallback(() => {
		const filter = titleFilter.trim() || undefined;
		setSubmittedFilter(filter);
		getGeneratedArtifacts({ titleFilter: filter });

		if (filter) {
			services.track.ampliTrackFeatureSearchSubmittedEvent({
				pageSource: aiAssistantPageSource.myContent,
				searchTerm: filter,
			});
		}
	}, [titleFilter]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	const handleClear = useCallback(() => {
		setTitleFilter('');
		setSubmittedFilter(undefined);
		getGeneratedArtifacts();
	}, []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	const loadMore = useCallback(() => {
		getGeneratedArtifacts({
			loadMore: true,
			titleFilter: submittedSearchTitle,
		});
	}, [submittedSearchTitle]);

	const isLoadingFirstBatch = [AiLoadingStatus.INITIAL, AiLoadingStatus.NONE].includes(
		loadingStatus
	);

	const isPaginating = loadingStatus === AiLoadingStatus.PAGINATION;

	const isEmptyState = artifacts.length === 0;

	return (
		<MasterLayout customTopBarContent={!isSmallScreen && <MyContentsTopBarContent />}>
			{isSmallScreen && <MyContentsTopBarContent />}
			{showSearchBar && (
				<Box
					sx={{
						position: 'sticky',
						top: 0,
						zIndex: 1,
						display: 'flex',
						justifyContent: 'center',
						padding: `${spaces.xl} ${contentPadding}`,
						backgroundColor: palette.background.paperElevation2,
					}}
				>
					<MyContentsSearchBar
						value={titleFilter}
						setValue={setTitleFilter}
						onSearch={handleSearch}
						onClear={handleClear}
					/>
				</Box>
			)}
			<Box
				data-testid="my-contents-page"
				sx={{
					padding: contentPadding,
					backgroundColor: palette.background.paperElevation1,
					minHeight: '100%',
				}}
			>
				{isLoadingFirstBatch && <SearchResultsLoader />}

				{!isLoadingFirstBatch && isEmptyState && !showSearchBar && <MyContentsEmptyState />}

				{!isLoadingFirstBatch && isEmptyState && showSearchBar && (
					<MyContentsEmptySearchState submittedSearchTitle={submittedSearchTitle || ''} />
				)}

				{!isLoadingFirstBatch && !isEmptyState && (
					<>
						<InfiniteScroll
							loadMore={loadMore}
							hasMore={hasNext}
							useWindow={false}
							getScrollParent={() => mainContentContainer}
						>
							<Box
								data-testid="artifact-cards-grid"
								sx={{
									display: 'grid',
									gap: spaces.md,
									gridTemplateColumns: {
										xs: '1fr',
										sm: 'repeat(2, 1fr)',
										md: 'repeat(3, 1fr)',
										lg: 'repeat(4, 1fr)',
									},
								}}
							>
								{artifacts.map((artifact) => (
									<ArtifactCard key={artifact.id} artifact={artifact} />
								))}
							</Box>
						</InfiniteScroll>
						{isPaginating && <SearchResultsLoader />}
					</>
				)}
			</Box>
		</MasterLayout>
	);
};

export default MyContentsPage;
